import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Box, useMediaQuery, useTheme, Dialog } from "@mui/material";
import PageContainer from "../../common_components/PageContainer";
import VisibleClientsDropdown from "../../common_components/VisibleClientsDropdown";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import DateRangeTabs from "./components/DateRangeTabs";
import ClientPerformanceTable from "./components/ClientPerformanceTable";
import ClientPerformanceMobileCards from "./components/ClientPerformanceMobileCards";
import TableToolbar from "../../common_components/TableToolbar";
import { NETWORK_ICON_MAP } from "../../utils/networkConstants";
import { fetchAllAccountSummary, selectAllClients, selectAllClientsLoading, // Updated selector
selectAccountSummaryLastUpdated, fetchVisibleClients, selectVisibleClients, hideClients, unhideClient } from "../../redux/accountSummarySlice";
import { selectToken } from "../../redux/authSlice";
import { setActiveModuleKey, clearActiveModuleKey } from "../../redux/userDataSlice";
import CommonLoader from "../../common_components/CommonLoader";
import { useLayout } from "../../contexts/LayoutContext";
const ACCOUNT_SUMMARY_MODULE_KEY = "account_summary";
const getClientIcon = (row) => {
    if (row.primary_channel) {
        if (row.primary_channel.includes("shopify"))
            return NETWORK_ICON_MAP.shopify;
        if (row.primary_channel.includes("ga"))
            return NETWORK_ICON_MAP.ga;
        if (row.primary_channel.includes("meta"))
            return NETWORK_ICON_MAP.meta;
        if (row.primary_channel.includes("adword") || row.primary_channel.includes("google ads"))
            return NETWORK_ICON_MAP.adword;
    }
    const networks = row.networks || [];
    const isShopify = row.has_shopify === true;
    const isGA = networks.some((n) => ["ga", "analytics", "google analytics"].includes(n.network?.toLowerCase()));
    if (isShopify)
        return NETWORK_ICON_MAP.shopify;
    if (!isShopify && isGA)
        return NETWORK_ICON_MAP.ga;
    return null;
};
const AccountSummaryMain = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 768px
    const [pageLoading, setPageLoading] = useState(true);
    const { setIsSidebarExpanded } = useLayout();
    // Redux selectors
    const allClients = useSelector(selectAllClients);
    const loading = useSelector(selectAllClientsLoading); // Using specific loading for all clients
    const lastUpdated = useSelector(selectAccountSummaryLastUpdated);
    const visibleClients = useSelector(selectVisibleClients);
    const authToken = useSelector(selectToken);
    // Local state
    const [clients, setClients] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState("today");
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [tableSearch, setTableSearch] = useState("");
    const [isFullScreen, setIsFullScreen] = useState(false);
    // Helper to get date range from tab
    const getDateRange = (range) => {
        // date the backend hadn't rolled over to yet, returning $0.00 stats.
        const today = new Date();
        const formatDate = (date) => {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        let startDate;
        let endDate = formatDate(today);
        switch (range) {
            case "today":
                startDate = formatDate(today);
                break;
            case "yesterday":
                const yesterday = new Date(today);
                yesterday.setUTCDate(yesterday.getUTCDate() - 1);
                startDate = formatDate(yesterday);
                endDate = formatDate(yesterday);
                break;
            case "last7days": {
                // MTD-1: 7 full days ending yesterday — today is excluded since it's incomplete
                const last7Days = new Date(today);
                last7Days.setUTCDate(last7Days.getUTCDate() - 7);
                const last7End = new Date(today);
                last7End.setUTCDate(last7End.getUTCDate() - 1);
                startDate = formatDate(last7Days);
                endDate = formatDate(last7End);
                break;
            }
            case "last30days": {
                // MTD-1: 30 full days ending yesterday — today is excluded since it's incomplete
                const last30Days = new Date(today);
                last30Days.setUTCDate(last30Days.getUTCDate() - 30);
                const last30End = new Date(today);
                last30End.setUTCDate(last30End.getUTCDate() - 1);
                startDate = formatDate(last30Days);
                endDate = formatDate(last30End);
                break;
            }
            default:
                startDate = formatDate(today);
        }
        return { startDate, endDate };
    };
    // Ensure module key is set before any API calls (visible-clients requires x-module-key)
    useEffect(() => {
        dispatch(setActiveModuleKey(ACCOUNT_SUMMARY_MODULE_KEY));
        return () => {
            dispatch(clearActiveModuleKey());
        };
    }, [dispatch]);
    // Collapse the sidebar submenu on visit — mobile only, desktop is unaffected.
    useEffect(() => {
        if (isMobile) {
            setIsSidebarExpanded(false);
        }
    }, [isMobile, setIsSidebarExpanded]);
    // Initial data fetch
    useEffect(() => {
        fetchInitialData();
    }, [dispatch, authToken]);
    // Update clients list when active clients or hidden clients change
    useEffect(() => {
        const list = visibleClients.map(item => ({
            id: item._id,
            name: item.name,
            visible: item.visible // ✅ Use API provided visibility
        }));
        setClients(list);
    }, [visibleClients]);
    // No refetch on tab switch: the list response already includes stats for
    // every period (today/yesterday/7days/30days) in one payload, computed from
    // fixed rolling windows that don't depend on the requested startDate/endDate
    // at all. Switching tabs is a pure client-side re-read of the same data
    // (see tableData below) — no network call needed.
    const fetchInitialData = async () => {
        try {
            await dispatch(fetchVisibleClients({ moduleKey: ACCOUNT_SUMMARY_MODULE_KEY })).unwrap();
            const { startDate, endDate } = getDateRange(selectedDateRange);
            await dispatch(fetchAllAccountSummary({ startDate, endDate })).unwrap();
            setInitialDataLoaded(true);
        }
        catch (error) {
            console.error("Failed to fetch initial data:", error);
        }
        finally {
            setPageLoading(false); // ✅ YAHI
        }
    };
    const handleRefresh = async () => {
        try {
            const { startDate, endDate } = getDateRange(selectedDateRange);
            await dispatch(fetchAllAccountSummary({ startDate, endDate, refreshData: true })).unwrap();
        }
        catch (error) {
            console.error("Refresh failed", error);
            // You might want to show an error notification here
        }
    };
    // Handle date range change — purely client-side, no fetch (see note above tableData).
    const handleDateRangeChange = (newRange) => {
        setSelectedDateRange(newRange);
    };
    const handleClientToggle = async (clientId, visible) => {
        setToggleLoading(true);
        try {
            if (!visible) {
                await dispatch(hideClients({
                    client_ids: [clientId],
                    reason: null,
                    moduleKey: ACCOUNT_SUMMARY_MODULE_KEY,
                })).unwrap();
            }
            else {
                await dispatch(unhideClient({ clientId, moduleKey: ACCOUNT_SUMMARY_MODULE_KEY })).unwrap();
            }
            await dispatch(fetchVisibleClients({ moduleKey: ACCOUNT_SUMMARY_MODULE_KEY })).unwrap();
            const { startDate, endDate } = getDateRange(selectedDateRange);
            await dispatch(fetchAllAccountSummary({ startDate, endDate })).unwrap();
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setToggleLoading(false);
        }
    };
    const handleBulkToggle = async (ids, visible) => {
        setToggleLoading(true);
        try {
            if (!visible) {
                await dispatch(hideClients({
                    client_ids: ids,
                    reason: null,
                    moduleKey: ACCOUNT_SUMMARY_MODULE_KEY,
                })).unwrap();
            }
            else {
                await dispatch(unhideClient({ client_ids: ids, moduleKey: ACCOUNT_SUMMARY_MODULE_KEY })).unwrap();
            }
            await dispatch(fetchVisibleClients({ moduleKey: ACCOUNT_SUMMARY_MODULE_KEY })).unwrap();
            const { startDate, endDate } = getDateRange(selectedDateRange);
            await dispatch(fetchAllAccountSummary({ startDate, endDate })).unwrap();
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setToggleLoading(false);
        }
    };
    // Transform data for the table
    const tableData = useMemo(() => {
        if (!Array.isArray(allClients) || allClients.length === 0)
            return [];
        const hiddenIds = new Set(visibleClients.filter(c => !c.visible).map(c => c._id));
        const mapKey = {
            today: 'today',
            yesterday: 'yesterday',
            last7days: '7days',
            last30days: '30days'
        };
        const mapped = allClients
            .filter(client => {
            const id = client.client_id || client._id;
            const isNotHidden = id === "all_clients" || !hiddenIds.has(id);
            const matchesSearch = tableSearch
                ? (client.client_name || client.name || "").toLowerCase().includes(tableSearch.toLowerCase())
                : true;
            return isNotHidden && matchesSearch;
        })
            .map((client) => {
            const statsKey = mapKey[selectedDateRange];
            const periodStats = client.stats?.[statsKey];
            const curRev = Number(periodStats?.rev ?? 0);
            const prevRev = Number(periodStats?.prevrev ?? 0);
            const diffValue = Number(periodStats?.prevdiff ?? 0);
            const diffPercent = prevRev > 0.01 ? (diffValue / prevRev) * 100 : (curRev > 0 ? 100 : 0);
            const projectedRev = Number(periodStats?.projection ?? 0);
            const channelList = client.channel || [];
            // Variance % = ((current - projected) / projected) * 100
            const projectedVariance = projectedRev > 0
                ? ((curRev - projectedRev) / projectedRev) * 100
                : 0;
            return {
                id: client.client_id || client._id,
                _id: client.client_id || client._id,
                name: client.client_name || client.name,
                overall_stats: {
                    total_revenue: curRev,
                    previous_total_revenue: prevRev
                },
                has_shopify: client.has_shopify === true,
                status: client.status,
                revenueChange: diffPercent,
                differenceChange: diffPercent,
                differenceValue: prevRev,
                channel: channelList,
                projectedRevenue: projectedRev,
                projectedVariance: projectedVariance
            };
        });
        // ⭐ All Clients fix top
        const allClientsRow = mapped.find(r => r.id === "all_clients");
        const otherClients = mapped.filter(r => r.id !== "all_clients");
        if (allClientsRow) {
            let sumCurRev = 0;
            let sumPrevRev = 0;
            for (const client of otherClients) {
                sumCurRev += client.overall_stats.total_revenue || 0;
                sumPrevRev += client.overall_stats.previous_total_revenue || 0;
            }
            allClientsRow.overall_stats.total_revenue = sumCurRev;
            allClientsRow.overall_stats.previous_total_revenue = sumPrevRev;
            const diffValue = sumCurRev - sumPrevRev;
            const diffPercent = sumPrevRev > 0.01 ? (diffValue / sumPrevRev) * 100 : (sumCurRev > 0 ? 100 : 0);
            allClientsRow.revenueChange = diffPercent;
            allClientsRow.differenceChange = diffPercent;
            allClientsRow.differenceValue = sumPrevRev;
        }
        otherClients.sort((a, b) => b.differenceChange - a.differenceChange);
        return allClientsRow
            ? [allClientsRow, ...otherClients]
            : otherClients;
    }, [allClients, selectedDateRange, visibleClients, tableSearch]);
    const filteredTableData = useMemo(() => {
        if (!tableSearch)
            return tableData;
        const lowerSearch = tableSearch.toLowerCase();
        return tableData.filter((client) => client.name?.toLowerCase().includes(lowerSearch));
    }, [tableData, tableSearch]);
    if (pageLoading) {
        return (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center" }, children: _jsx(CommonLoader, { className: 'table-loader' }) }));
    }
    return (_jsx(PageContainer, { children: _jsxs(Paper, { elevation: 0, className: "account-summary-container", sx: { display: "flex", flexDirection: "column", height: "calc(100vh - 100px)", overflow: "hidden" }, children: [_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, onRefresh: handleRefresh, showAgo: true }), rightSection: _jsx(VisibleClientsDropdown, { clients: clients, onChange: setClients, onToggle: handleClientToggle, onBulkToggle: handleBulkToggle, currentDateRange: getDateRange(selectedDateRange) }) }) }), _jsxs(Box, { sx: { flex: 1, display: "flex", flexDirection: "column", position: "relative", minHeight: 0, mt: 1 }, children: [isFullScreen ? (_jsx(Dialog, { fullScreen: true, open: isFullScreen, onClose: () => setIsFullScreen(false), children: _jsxs(Box, { className: "account-summary-scroll-body", sx: {
                                    flex: 1,
                                    overflowY: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    bgcolor: "#f8fafc"
                                }, children: [_jsxs(Box, { className: "account-summary-tabs-sticky", sx: {
                                            position: "sticky", top: 0, zIndex: 10, bgcolor: "#fff",
                                            display: "flex",
                                            flexDirection: isMobile ? "column" : "row",
                                            justifyContent: "space-between",
                                            alignItems: isMobile ? "stretch" : "center",
                                            borderBottom: isMobile ? "1px solid #F1F5F9" : "none",
                                        }, children: [_jsx(DateRangeTabs, { selectedRange: selectedDateRange, onChange: handleDateRangeChange, disabled: loading || toggleLoading, sx: { m: "10px" } }), _jsx(Box, { sx: { pr: 1, display: "flex", justifyContent: isMobile ? "flex-end" : "unset" }, children: _jsx(TableToolbar, { title: "", showSearch: true, showExpand: false, showFullScreen: true, searchValue: tableSearch, onSearchChange: setTableSearch, searchPlaceholder: "Search by client name...", onFullScreenClick: () => setIsFullScreen(!isFullScreen) }) })] }), isMobile ? (_jsx(ClientPerformanceMobileCards, { data: filteredTableData, loading: loading || toggleLoading, globalDateRange: selectedDateRange })) : (_jsx(ClientPerformanceTable, { data: filteredTableData, loading: loading || toggleLoading, globalDateRange: selectedDateRange, onGlobalDateRangeChange: handleDateRangeChange }))] }) })) : (_jsxs(Box, { className: "account-summary-scroll-body", sx: {
                                flex: 1,
                                overflowY: "auto",
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                                display: "flex",
                                flexDirection: "column",
                                bgcolor: "#fff"
                            }, children: [_jsxs(Box, { className: "account-summary-tabs-sticky", sx: {
                                        position: "sticky", top: 0, zIndex: 10, bgcolor: "#fff",
                                        display: "flex",
                                        flexDirection: isMobile ? "column" : "row",
                                        justifyContent: "space-between",
                                        alignItems: isMobile ? "stretch" : "center",
                                        borderBottom: isMobile ? "1px solid #F1F5F9" : "none",
                                    }, children: [_jsx(DateRangeTabs, { selectedRange: selectedDateRange, onChange: handleDateRangeChange, disabled: loading || toggleLoading, sx: { m: "10px" } }), _jsx(Box, { sx: { pr: 1, display: "flex", justifyContent: isMobile ? "flex-end" : "unset" }, children: _jsx(TableToolbar, { title: "", showSearch: true, showExpand: false, showFullScreen: true, searchValue: tableSearch, onSearchChange: setTableSearch, searchPlaceholder: "Search by client name...", onFullScreenClick: () => setIsFullScreen(!isFullScreen) }) })] }), isMobile ? (_jsx(ClientPerformanceMobileCards, { data: filteredTableData, loading: loading || toggleLoading, globalDateRange: selectedDateRange })) : (_jsx(ClientPerformanceTable, { data: filteredTableData, loading: loading || toggleLoading, globalDateRange: selectedDateRange, onGlobalDateRangeChange: handleDateRangeChange }))] })), (loading || toggleLoading) && (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) }))] })] }) }));
};
export default AccountSummaryMain;
